import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { echotwinApi } from "@/lib/echotwin-api";
import { auctionItems as mockAuctionItems } from "@/mocks/auctionItems";

/**
 * 전문가 PICK 물건 — kangstudy_auction_items 를 읽는다.
 *
 * 그 테이블은 두 종류의 행을 담는다:
 *  - source='auto'   : kangstudy_sync_auction_items() 가 EchoTwin auction_cases 에서
 *                      매일 06:30 KST 에 큐레이션해 넣는다(신건 제외·치명위험 0·
 *                      위험원천 확인 완료만).
 *  - source='manual' : 관리자가 엑셀로 직접 등록. 싱크가 건드리지 않는다.
 *
 * DB 는 snake_case, 화면은 camelCase 라 여기서 한 번만 옮긴다.
 *
 * ⚠️ 조회 실패 시 목업으로 폴백하되 `usingMock` 을 같이 돌려준다. 조용히
 * 폴백하면 테이블 이름이 바뀌거나 RLS 가 막혔을 때 "글이 좀 적네" 로만 보이고
 * 아무도 못 알아챈다(2026-08-18 개명 사고가 정확히 그랬다).
 */
export interface AuctionItemView {
  id: string;
  caseNumber: string;
  court: string;
  location: string;
  propertyType: string;
  appraisedValue: number;
  minimumPrice: number;
  auctionDate: string;
  failureCount: number;
  hasTenant: boolean;
  rightsAnalysis: string;
  /** 시세 추정이 없으면 null — 감정가를 시세로 대체하지 않는다. */
  expectedSalePrice: number | null;
  /** 시세 − 최저가×1.07(취득비용). 시세가 없으면 null. */
  expectedProfit: number | null;
  grade: string;
  exclusiveArea: number | null;
  buildingAge: number | null;
  landShare: string | null;
  floorInfo: string | null;
  imageUrl?: string;
  /** EchoTwin auction_cases.id(source='auto'만 존재) — public case-preview/case-photo 호출 키. */
  sourceCaseId: string | null;
}

/** GET /api/v1/public/case-preview/:id — 여기서 쓰는 필드만 발췌. */
interface CasePreviewData {
  marketEstEok?: number | null;
  photoSeqs?: number[];
}

function toView(r: Record<string, unknown>): AuctionItemView {
  const num = (v: unknown): number | null =>
    v === null || v === undefined || v === "" ? null : Number(v);
  const caseNumber = (r.case_number as string) || "";
  const sourceCaseId = (r.source_case_id as string) || null;
  return {
    id: String(r.id),
    caseNumber,
    court: (r.court as string) || "",
    location: (r.location as string) || "",
    propertyType: (r.property_type as string) || "",
    appraisedValue: num(r.appraised_value) ?? 0,
    minimumPrice: num(r.minimum_price) ?? 0,
    auctionDate: (r.auction_date as string) || "",
    failureCount: num(r.failure_count) ?? 0,
    hasTenant: Boolean(r.has_tenant),
    rightsAnalysis: (r.rights_analysis as string) || "",
    expectedSalePrice: num(r.expected_sale_price),
    expectedProfit: num(r.expected_profit),
    grade: (r.grade as string) || "C",
    exclusiveArea: num(r.exclusive_area),
    buildingAge: num(r.building_age),
    landShare: (r.land_share as string) || null,
    floorInfo: (r.floor_info as string) || null,
    // 사진은 EchoTwin 원본 케이스 id(source_case_id)로만 찾을 수 있다 — case_number
    // 로는 안 된다(형식이 다르다). 없으면 초기엔 플레이스홀더, enrichWithEchotwin 이
    // casePreview 로 photoSeqs 를 받아온 뒤 실제 사진 URL로 갱신한다.
    imageUrl: sourceCaseId ? echotwinApi.casePhotoUrl(sourceCaseId, 0) : undefined,
    sourceCaseId,
  };
}

/**
 * 목록에 뜬 실데이터 항목을 EchoTwin 공개 프리뷰(case-preview)로 보강한다 —
 * 실제 사진(photoSeqs)과, DB 동기화 시점엔 없었던 시세 추정을 채운다.
 * source_case_id 가 있는(=source:'auto') 항목만 대상. 실패한 항목은 조용히
 * 건너뛴다(플레이스홀더 유지) — 이 보강이 실패했다고 목록 전체가 깨지면 안 된다.
 */
async function enrichWithEchotwin(
  items: AuctionItemView[],
  onUpdate: (id: string, patch: Partial<AuctionItemView>) => void,
) {
  const targets = items.filter((i) => i.sourceCaseId);
  await Promise.allSettled(
    targets.map(async (item) => {
      const res = await echotwinApi.casePreview(item.sourceCaseId!);
      const data = (res as { data?: CasePreviewData })?.data;
      if (!data) return;

      const patch: Partial<AuctionItemView> = {};
      if (data.photoSeqs && data.photoSeqs.length > 0) {
        patch.imageUrl = echotwinApi.casePhotoUrl(item.sourceCaseId!, data.photoSeqs[0]);
      }
      if (item.expectedSalePrice == null && typeof data.marketEstEok === "number") {
        const salePrice = Math.round(data.marketEstEok * 1e8);
        patch.expectedSalePrice = salePrice;
        patch.expectedProfit = Math.round(salePrice - item.minimumPrice * 1.07);
      }
      if (Object.keys(patch).length > 0) onUpdate(item.id, patch);
    }),
  );
}

/** 목업(camelCase, 일부 필드 없음)을 같은 뷰 모양으로 맞춘다. */
function mockToView(m: (typeof mockAuctionItems)[number]): AuctionItemView {
  return {
    id: m.id,
    caseNumber: m.caseNumber,
    court: m.court,
    location: m.location,
    propertyType: m.propertyType,
    appraisedValue: m.appraisedValue,
    minimumPrice: m.minimumPrice,
    auctionDate: m.auctionDate,
    failureCount: m.failureCount,
    hasTenant: m.hasTenant,
    rightsAnalysis: m.rightsAnalysis,
    expectedSalePrice: m.expectedSalePrice ?? null,
    expectedProfit: m.expectedProfit ?? null,
    grade: m.grade,
    exclusiveArea: m.exclusiveArea ?? null,
    buildingAge: m.buildingAge ?? null,
    landShare: null,
    floorInfo: null,
    imageUrl: m.imageUrl,
    sourceCaseId: null,
  };
}

export function useAuctionItems() {
  const [items, setItems] = useState<AuctionItemView[]>([]);
  const [loading, setLoading] = useState(true);
  const [usingMock, setUsingMock] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      // 입찰일이 지난 물건은 더 이상 응찰할 수 없다 — 목록에서 제외.
      // toISOString()은 UTC라 KST 자정~오전 9시 사이엔 하루 전 날짜가 나와
      // 이미 지난 물건이 계속 보이는 버그가 있었다 — 로컬(브라우저) 날짜로 계산.
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const { data, error } = await supabase
        .from("kangstudy_auction_items")
        .select("*")
        .gte("auction_date", today)
        .order("auction_date", { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        const viewItems = data.map((r) => toView(r as Record<string, unknown>));
        setItems(viewItems);
        setUsingMock(false);
        // 목록은 먼저 그리고, 사진·시세는 백그라운드로 하나씩 채워 넣는다.
        void enrichWithEchotwin(viewItems, (id, patch) => {
          setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
        });
      } else {
        setItems(mockAuctionItems.map(mockToView));
        setUsingMock(true);
      }
    } catch (e) {
      console.error("[kangstudy] 전문가 PICK 조회 실패 — 목업으로 폴백합니다:", e);
      setItems(mockAuctionItems.map(mockToView));
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return { items, loading, usingMock, refetch: fetchItems };
}
